/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Grid component to display all routes per client in Data of Dedicated Delivery page.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import CachedIcon from "@mui/icons-material/Cached";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import agent from "../../../../app/api/agent";
import getAxiosError from "../../../../app/api/axiosError";
import { getRouteData } from "../../../../app/features/dedicatedDelivery/routeSlice";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import { DdRouteDashboardResponse } from "../../../../app/models/responseModels/DdRouteResponse";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../../../components/common/filter/GridDataFilter";
import Loader from "../../../../components/common/loader/Loader";
import { CustomTooltip } from "../../../../components/common/tooltip/CustomToolTip";
import {
  ClientStoreProps,
  DD_ROUTE_COLUMN_NAME,
  DD_ROUTE_LABEL_NAME,
  ddRouteGridFilter,
} from "../../DdTypes";
import { getDefaultMRTOptions } from "../../DefaultMrtOptions";
import { ImportRouteData } from "../../ImportRouteData";
import { format, parse } from "date-fns";
import { debounce } from "../../../../components/common/utils/Debounce";
import { axiosRequests } from "../../../../app/api/axiosRequest";
import { ApiResponse } from "../../../../app/models/ApiResponse";

// component for Data page of dedicated delivery
const ClientRoute = (props: ClientStoreProps) => {
  // to dispatch data to store
  const dispatch = useAppDispatch();

  // import base coniguration for grid
  const defaultMRTOptions = getDefaultMRTOptions<DdRouteDashboardResponse>();

  // fetch routeData from store
  const { isLoading, routeData } = useSelector(
    (state: RootState) => state.dedicatedDeliveryFetchRoute
  );

  // state to manage pagination values from grid
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  // state to manage startDate filter on the page
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(props.startDate),
    new DateObject().setDate(props.endDate),
  ]);

  // flag to manage loader when component is busy in server call
  const [dataFetching, setDataFetching] = useState<boolean>(true);

  const [exporting, setExporting] = useState<boolean>(false);

  const [resetFilter, setResetFilter] = useState<boolean>(false);

  const [filterableColumnsState, setFilterableColumnsState] =
    useState<FilterableColumnsState>({});

  // access to passed paramter to this component
  const clientId = props.clientId;
  const clientName = props.clientName;

  // change page title when use visit on this page
  useEffect(() => {
    document.title = "Dedicated Delivery - Data";
  }, []);

  const debouncedUpdateTotalOrders = debounce((routeId: string, value: number) => {
    saveTotalOrders(routeId, value);
  }, 300);

  const debouncedUpdateOrdersDelivered = debounce((routeId: string, value: number) => {
    saveOrdersDelivered(routeId, value);
  }, 300);

  const saveTotalOrders = async (routeId: string, value: number) => {
    try {
      const response = await agent.DedicatedDeliveryAgent.updateTotalOrdersForRoute(routeId, value)
      if (response.isSuccess) {
        toast.success("Total Orders updated successfully",  {
          toastId: "totalOrdersSuccess",
        });
      } else {
        toast.error("Failed to update Total Orders: " + response.message, {
          toastId: "totalOrdersFailure",
        });
      }
    }
      catch (error) {
      const appError = getAxiosError(error);
      toast.error("Error updating Total Orders: " + appError.errorMessage, {
        toastId: "totalOrdersException",
      });
    }
  };

  const saveOrdersDelivered = async (routeId: string, value: number) => {
    try {
      const response = await agent.DedicatedDeliveryAgent.updateOrdersDeliveredForRoute(routeId, value);
      if (response.isSuccess) {
        toast.success("Orders Delivered updated successfully", {
          toastId: "ordersDeliveredSuccess",
        });
      } else {
        toast.error("Failed to update Orders Delivered: " + response.message, {
          toastId: "ordersDeliveredFailure",
        });
      }
    }
      catch (error) {
      const appError = getAxiosError(error);
      toast.error("Error updating Orders Delivered: " + appError.errorMessage, {
        toastId: "ordersDeliveredException",
      });
    }
  };

  // fetch routes all routes per client from server
  const getRoutes = useCallback(
    async (filterableColumnsState: FilterableColumnsState) => {
      try {
        const response = await dispatch(
          getRouteData({
            clientId,
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            startDate: dateRange[0].format("YYYY-MM-DD"),
            endDate: dateRange[1].format("YYYY-MM-DD"),
            filters: filterableColumnsState,
          })
        ).unwrap();
        if (!response.isSuccess) {
          toast.error(response.message!, {
            toastId: "storeData",
          });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "storeDataException",
        });
      } finally {
        setDataFetching(false);
        setResetFilter(false);
      }
    },
    [dispatch, pagination, dateRange, clientId]
  );

  // incase flag is true then call to server to get all routes
  useEffect(() => {
    if (dataFetching) {
      getRoutes(filterableColumnsState);
    }
  }, [dataFetching, getRoutes, filterableColumnsState]);

  // call to server when user changes page inside grid
  useEffect(() => {
    setDataFetching(true);
  }, [pagination.pageIndex, pagination.pageSize]);

  // column configuration to be rendered on grid
  const columns = useMemo<MRT_ColumnDef<DdRouteDashboardResponse>[]>(
    () => [
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ID,
        header: DD_ROUTE_LABEL_NAME.ID,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DATE,
        header: DD_ROUTE_LABEL_NAME.DATE,
        enableEditing: false,
        Cell: ({ cell, row }) => {
          const rawDate = cell.getValue<string>();
          return format(parse(rawDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy");
        },
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.STOREID,
        header: DD_ROUTE_LABEL_NAME.STOREID,
        enableEditing: false,
        Cell: ({ cell, row }) => row.original.store.clientStoreId,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.TRIPID,
        header: DD_ROUTE_LABEL_NAME.TRIPID,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERID,
        header: DD_ROUTE_LABEL_NAME.DRIVERID,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERNAME,
        header: DD_ROUTE_LABEL_NAME.DRIVERNAME,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERREQUEST,
        header: DD_ROUTE_LABEL_NAME.DRIVERREQUEST,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.driverRequest
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ENROUTETOPICKUP,
        header: DD_ROUTE_LABEL_NAME.ENROUTETOPICKUP,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.enrouteToPickUp
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ONROUTEENROUTETOPICKUP,
        header: DD_ROUTE_LABEL_NAME.ONROUTEENROUTETOPICKUP,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.onRouteEnrouteToPickUp ? "TRUE" : "FALSE",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ATPICKUP,
        header: DD_ROUTE_LABEL_NAME.ATPICKUP,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.atPickUp
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ONTIMEARRIVAL,
        header: DD_ROUTE_LABEL_NAME.ONTIMEARRIVAL,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.onTimeArrival ? "TRUE" : "FALSE",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ASSOCIATEDISPENSE,
        header: DD_ROUTE_LABEL_NAME.ASSOCIATEDISPENSE,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.associateDispense
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.LOADSTART,
        header: DD_ROUTE_LABEL_NAME.LOADSTART,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.loadStart
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERDWELLTIME,
        header: DD_ROUTE_LABEL_NAME.DRIVERDWELLTIME,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.LOADEND,
        header: DD_ROUTE_LABEL_NAME.LOADEND,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.loadEnd
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERLOADTIME,
        header: DD_ROUTE_LABEL_NAME.DRIVERLOADTIME,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DEPARTURE,
        header: DD_ROUTE_LABEL_NAME.DEPARTURE,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.departure
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ONTIMEDEPARTURE,
        header: DD_ROUTE_LABEL_NAME.ONTIMEDEPARTURE,
        enableEditing: false,
        Cell: ({ cell, row }) => (row.original.departure ? "TRUE" : "FALSE"),
      },

      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERLASTDELIVERY,
        header: DD_ROUTE_LABEL_NAME.DRIVERLASTDELIVERY,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.driverLastDelivery
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.TRIPACTUALTIME,
        header: DD_ROUTE_LABEL_NAME.TRIPACTUALTIME,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.DRIVERDOORSTEPTIME,
        header: DD_ROUTE_LABEL_NAME.DRIVERDOORSTEPTIME,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ENROUTETORETURN,
        header: DD_ROUTE_LABEL_NAME.ENROUTETORETURN,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.enrouteToReturn
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ATRETURN,
        header: DD_ROUTE_LABEL_NAME.ATRETURN,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.atReturn
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.TRIPMAKEDCOMPLETE,
        header: DD_ROUTE_LABEL_NAME.TRIPMAKEDCOMPLETE,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.tripMakedComplete
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.TRIPPLANNED,
        header: DD_ROUTE_LABEL_NAME.TRIPPLANNED,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.tripPlanned
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.TOTALORDERS,
        header: DD_ROUTE_LABEL_NAME.TOTALORDERS,
        enableEditing: true,
        muiEditTextFieldProps: ({cell, row, table}) => ({
         inputProps: {
            type: "number",
            placeholder: "Enter a number",
            min: 0,
            max: 999,
          },
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              event.preventDefault();

              const target = (event.target as HTMLInputElement).value.trim();;
              console.log("Target value:", target);

              const isValid = /^[0-9]+$/.test(target) && Number(target) >= 0 && Number(target) <= 999;
               if (isValid) {
                debouncedUpdateTotalOrders(row?.original?.tripId ?? 0, parseInt(target, 10));
              } else {
                toast.error("Please enter a valid integer value", {
                  toastId: "integerValidationError",
                });
              }
            }
          }
        })
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ORDERSDELIVERED,
        header: DD_ROUTE_LABEL_NAME.ORDERSDELIVERED,
        enableEditing: true,
        muiEditTextFieldProps: ({cell, row, table}) => ({
         inputProps: {
            type: "number",
            placeholder: "Enter a number",
            min: 0,
            max: 999,
          },
          onKeyDown: (event) => {
            if (event.key === "Enter") {
              event.preventDefault();

              const target = (event.target as HTMLInputElement).value.trim();;
              console.log("Target value:", target);

              const isValid = /^[0-9]+$/.test(target) && Number(target) >= 0 && Number(target) <= 999;
               if (isValid) {
                debouncedUpdateOrdersDelivered(row?.original?.tripId ?? 0, parseInt(target, 10));
              } else {
                toast.error("Please enter a valid integer value", {
                  toastId: "integerValidationError",
                });
              }
            }
          }
        })
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ORDERSUNDELIVERED,
        header: DD_ROUTE_LABEL_NAME.ORDERSUNDELIVERED,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ORDERSUNDELIVEREDNOATTEMPT,
        header: DD_ROUTE_LABEL_NAME.ORDERSUNDELIVEREDNOATTEMPT,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.ORDERSUNDELIVEREDATTEMPT,
        header: DD_ROUTE_LABEL_NAME.ORDERSUNDELIVEREDATTEMPT,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.MILES,
        header: DD_ROUTE_LABEL_NAME.MILES,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.UNITSTOTAL,
        header: DD_ROUTE_LABEL_NAME.UNITSTOTAL,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.UNITSORDERSWITHUNITS,
        header: DD_ROUTE_LABEL_NAME.UNITSORDERSWITHUNITS,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.CREATEDON,
        header: DD_ROUTE_LABEL_NAME.CREATEDON,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.createdOn
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.UPDATEDON,
        header: DD_ROUTE_LABEL_NAME.UPDATEDON,
        enableEditing: false,
        Cell: ({ cell, row }) =>
          row.original.updatedOn
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.CREATEDBY,
        header: DD_ROUTE_LABEL_NAME.CREATEDBY,
        enableEditing: false,
      },
      {
        accessorKey: DD_ROUTE_COLUMN_NAME.UPDATEDBY,
        header: DD_ROUTE_LABEL_NAME.UPDATEDBY,
        enableEditing: false,
      },
    ],
    []
  );

  const handleRouteExport = async (routeIds?: number[] | undefined) => {
    try {
      setExporting(true);
      if (dateRange) {
        var formatStartDateP = dateRange[0].format("YYYY-MM-DD");
        var formatEndDateP = dateRange[1].format("YYYY-MM-DD");
        // Send GET request to Spring Boot backend
        const response = await agent.DedicatedDeliveryAgent.exportRouteData({
          clientId: clientId,
          routeIds: routeIds,
          startDate: formatStartDateP,
          endDate: formatEndDateP,
          filters: filterableColumnsState,
        });

        // Create a blob from the response data
        const blob = new Blob([response], { type: "text/csv" });

        // Create a download link and trigger click event
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.download =
          clientName +
          "-Routes-Report-" +
          formatStartDateP +
          "-to-" +
          formatEndDateP +
          ".csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Cleanup
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading file:" + error);
      // Handle error
    } finally {
      setExporting(false);
    }
  };

  // elements to be rendered on top of grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<DdRouteDashboardResponse>
  ) => {
    return (
      <Box>
        <CustomTooltip
          arrow
          title="Refresh"
          onClick={() => {
            getRoutes(filterableColumnsState);
          }}
        >
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          arrow
          title="Export"
          onClick={() => {
            // if no selection made, then all rows based on date filter is exported.
            if (
              !table.getIsSomeRowsSelected() &&
              !table.getIsAllRowsSelected()
            ) {
              handleRouteExport(undefined);
            } else {
              // if selection made, then all selected rows are exported.
              handleRouteExport(
                table.getSelectedRowModel().rows.map((row) => row.original.id)
              );
            }
          }}
        >
          <IconButton type="button">
            <FileDownloadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  const getValuesByFieldForGridFilter = (
    columnName: string,
    setClFilterableColumns: React.Dispatch<
      React.SetStateAction<FilterableColumns>
    >
  ) => {
    if (columnName) {
      agent.DedicatedDeliveryAgent.getValuesByField(
        dateRange[0].format("YYYY-MM-DD"),
        dateRange[1].format("YYYY-MM-DD"),
        columnName,
        clientId
      )
        .then((res) => {
          let resData = res.data;
          console.log(res.data);
          if (res.isSuccess && resData?.length) {
            resData = resData?.map((e) => e.toString());
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: resData ?? [],
                },
              };
            });
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error(
            "Request is failed while loading options, please try again later."
          );
        });
    }
  };

  // grid configuration to show Routes
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: routeData.routes,
    manualPagination: true,
    enableEditing: true,
    editDisplayMode: "cell",
    onPaginationChange: setPagination,
    enableRowSelection: true,
    rowCount: routeData.totalRows,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: false,
      },
    },
    getRowId: (row) => row.id.toString(),
    state: {
      pagination,
      showProgressBars: isLoading,
      sorting: [{ desc: true, id: "date" }],
    },
    //optionally, use single-click to activate editing mode instead of default double-click
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      onClick: () => {
        table.setEditingCell(cell); //set editing cell
        //optionally, focus the text field
        queueMicrotask(() => {
          const textField = table.refs.editInputRefs.current[column.id];
          if (textField) {
            textField.focus();
            textField.select?.();
          }
        });
      },
    }),
    // onColumnFiltersChange: setColumnFilters,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        const routePrimaryId = row.original.id;
        console.log(clientId, clientName, routePrimaryId);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="body1" component="p">
          Total Rows: {routeData.totalRows}
        </Typography>
      );
    },
  });
  return (
    <Grid container paddingY={2} rowSpacing={2} justifyContent="space-between">
      <Loader isLoading={exporting || dataFetching || isLoading} />
      <Grid item>
        <Typography component="h2" variant="h3">
          Client: {clientName}
        </Typography>
      </Grid>
      <Grid item>
        <Grid container item alignItems="center">
          <Box zIndex={100} style={{ overflowY: "auto" }}>
            <DatePicker
              format="MM/DD/YYYY"
              className="green"
              dateSeparator=" ~ "
              range
              rangeHover
              value={dateRange}
              onChange={(dateObjects: DateObject[]) => {
                setDateRange(dateObjects);
              }}
              maxDate={new Date()}
            />
          </Box>
          <Button
            size="large"
            onClick={() => {
              setDataFetching(true);
              setResetFilter(true);
            }}
          >
            SUBMIT
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        <ImportRouteData clientId={clientId} />
      </Grid>
      <Grid item xs={12}>
        <GridDataFilter
          filterableColumns={ddRouteGridFilter}
          title="Search Route Data"
          dateRange={dateRange}
          resetFlag={resetFilter}
          asyncSubmitFunction={(filterState: FilterableColumnsState) => {
            setFilterableColumnsState(filterState);
            getRoutes(filterState);
          }}
          asyncSelectedColumnValues={getValuesByFieldForGridFilter}
        />
      </Grid>
      <Grid item xs={12}>
        <MaterialReactTable table={table} />
      </Grid>
    </Grid>
  );
};
export default ClientRoute;
